/**
 * Dashboard.jsx — executive dashboard wired to real API.
 * All data comes from /api/dashboard/* — no mock imports.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { KpiCard, Badge, Card, CardHead, BarChart, DonutChart } from '../components/ui';
import { dashboard as dashApi, fmt } from '../services/api';

function useApi(fn, deps = []) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await fn(); setData(r.data ?? r); }
    catch (_) {}
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => { load(); }, [load]);
  return { data, loading, reload: load };
}

export const Dashboard = ({ onNavigate }) => {
  const { data: kpis, loading: kL } = useApi(() => dashApi.kpis());
  const { data: obls }              = useApi(() => dashApi.obligations());
  const { data: acts }              = useApi(() => dashApi.activity());
  const { data: mix  }              = useApi(() => dashApi.capitalMix());

  const k           = kpis || {};
  const legacyTotal = mix?.legacyPool?.total       || 0;
  const instTotal   = mix?.institutional?.total    || 0;
  const grandTotal  = legacyTotal + instTotal || 1;
  const legacyPct   = Math.round((legacyTotal / grandTotal) * 100);
  const instPct     = 100 - legacyPct;
  const deployedPct = k.utilisationPct || 0;
  const availPct    = Math.max(0, 100 - deployedPct);

  const oblDateStyle = (dateStr) => {
    if (!dateStr) return {};
    const days = Math.floor((new Date(dateStr) - new Date()) / 86400000);
    if (days <= 5)  return { background: 'var(--danger-bg)',  color: 'var(--danger)'  };
    if (days <= 14) return { background: 'var(--warning-bg)', color: 'var(--warning)' };
    return {};
  };

  const actIcon = (action = '') => {
    if (action.includes('deposit'))    return { icon: '⬇', bg: 'var(--success-bg)'   };
    if (action.includes('deal'))       return { icon: '◆', bg: 'var(--info-bg)'      };
    if (action.includes('repay'))      return { icon: '↑', bg: 'var(--success-bg)'   };
    if (action.includes('withdrawal')) return { icon: '↙', bg: 'var(--warning-bg)'   };
    if (action.includes('drawdown'))   return { icon: '⬇', bg: 'var(--accent-light)' };
    return { icon: '◎', bg: 'var(--surface2)' };
  };

  const val = (v) => kL ? '—' : v;

  return (
    <div>
      <div className="kpi-grid g4">
        <KpiCard label="Total capital under mgmt"  value={val(fmt(k.totalCapitalUnderManagement))} sub={val(`${k.activeInvestors||0} investors · ${k.activeFunders||0} funders`)} chip={val(`${k.utilisationPct||0}% utilised`)} chipType={k.utilisationPct>90?'warn':'up'} highlight highlightColor="brand" />
        <KpiCard label="Legacy pool balance"        value={val(fmt(k.legacyPoolBalance))}  sub={val(`${k.activeInvestors||0} investors · ${k.activeDeposits||0} deposits`)} chip={val(`${fmt(k.legacyAccruedReturns||0)} accrued`)} chipType="up"   highlight highlightColor="brand" />
        <KpiCard label="Institutional capital"      value={val(fmt(k.institutionalCapital))} sub={val(`${k.activeFunders||0} facilities · ${fmt(k.institutionalDrawn||0)} drawn`)} highlight highlightColor="brand" />
        <KpiCard label="Available capital"          value={val(fmt(k.availableCapital))} sub="Undeployed across all sources" highlight highlightColor="accent" />
      </div>
      <div className="kpi-grid g4">
        <KpiCard label="Deployed capital"     value={val(fmt(k.deployedCapital))}   sub={val(`${k.utilisationPct||0}% utilisation`)} />
        <KpiCard label="Returns due (30 days)" value={val(fmt(k.returnsDue30Days))} chip={val(`${k.returnsCycleCount||0} deposits cycling`)} chipType="warn" />
        <KpiCard label="Active deals"          value={val(k.activeDeals||0)}        sub={k.overdueDeals>0?`${k.overdueDeals} overdue`:'All on track'} />
        <KpiCard label="Investors / funders"   value={val(`${k.activeInvestors||0} / ${k.activeFunders||0}`)} sub="Legacy investors · Institutional funders" />
      </div>

      <div className="row3">
        <Card>
          <CardHead title="Capital mix & utilisation" sub="Legacy vs institutional · deployed vs available" action onAction={() => onNavigate('allocation')} actionLabel="Full view →" />
          <div className="card-body">
            <BarChart rows={[
              { label:'Legacy pool',    pct:legacyPct,   color:'var(--brand)',  val:`${fmt(legacyTotal)} · ${legacyPct}%`   },
              { label:'Institutional',  pct:instPct,     color:'var(--accent)', val:`${fmt(instTotal)} · ${instPct}%`      },
              { label:'Total deployed', pct:deployedPct, color:'var(--brand)',  val:`${fmt(k.deployedCapital||0)} · ${deployedPct}%` },
              { label:'Available',      pct:availPct,    color:'#378add',       val:`${fmt(k.availableCapital||0)} · ${availPct}%`  },
            ]} />
            <div style={{ marginTop:16 }}>
              <div className="section-label">Source split</div>
              <DonutChart legacy={legacyTotal} institutional={instTotal} />
            </div>
          </div>
        </Card>

        <div className="col">
          <Card>
            <CardHead title="Upcoming obligations" />
            <div className="card-body">
              {!obls && <div style={{padding:'8px 0',fontSize:12,color:'var(--text3)'}}>Loading…</div>}
              {obls?.length === 0 && <div style={{padding:'8px 0',fontSize:12,color:'var(--success)'}}>✓ No obligations due in the next 60 days</div>}
              {(obls||[]).slice(0,5).map((o,i) => (
                <div className="obl-item" key={i}>
                  <div className="obl-date" style={oblDateStyle(o.due_date)}>
                    {o.due_date ? new Date(o.due_date).toLocaleDateString('en-ZA',{day:'numeric',month:'short'}) : '—'}
                  </div>
                  <div className="obl-info">
                    <div className="obl-name">{o.description}</div>
                    <div className="obl-type">{o.type==='legacy_return'?'Legacy return':'Institutional repayment'}</div>
                  </div>
                  <div className="obl-amt">{fmt(o.amount)}</div>
                </div>
              ))}
            </div>
          </Card>

          <Card>
            <CardHead title="Deals requiring action" action onAction={() => onNavigate('deals')} actionLabel="View all" />
            <div className="card-body">
              {k.overdueDeals > 0
                ? <div className="act-item" style={{cursor:'pointer'}} onClick={() => onNavigate('deals')}>
                    <div className="act-icon" style={{background:'var(--danger-bg)'}}>⚠</div>
                    <div><div className="act-text"><b>{k.overdueDeals} deal{k.overdueDeals>1?'s':''} overdue</b></div><div className="act-time">Requires immediate follow-up</div></div>
                    <Badge type="danger">Overdue</Badge>
                  </div>
                : <div style={{padding:'8px 0',fontSize:12,color:'var(--success)'}}>✓ No deals require immediate action</div>
              }
            </div>
          </Card>
        </div>
      </div>

      <Card className="mb">
        <CardHead title="Recent activity" action actionLabel="View audit log →" onAction={() => onNavigate('audit')} />
        <div className="card-body">
          {!acts && <div style={{padding:'8px 0',fontSize:12,color:'var(--text3)'}}>Loading…</div>}
          {(acts||[]).slice(0,6).map((a,i) => {
            const {icon,bg} = actIcon(a.action||'');
            let after = {};
            try { after = JSON.parse(a.after_state||'{}'); } catch(_){}
            return (
              <div className="act-item" key={i}>
                <div className="act-icon" style={{background:bg}}>{icon}</div>
                <div style={{flex:1}}>
                  <div className="act-text"><b>{(a.action||'').replace('.',' ').replace(/_/g,' ')}</b>{a.record_ref?` — ${a.record_ref}`:''}</div>
                  <div className="act-time">{a.timestamp?new Date(a.timestamp).toLocaleString('en-ZA'):''} · {a.user_name||'System'}</div>
                </div>
                {after.amount && <div className="act-amt">{fmt(after.amount)}</div>}
              </div>
            );
          })}
          {acts?.length===0 && <div style={{padding:'8px 0',fontSize:12,color:'var(--text3)'}}>No recent activity</div>}
        </div>
      </Card>
    </div>
  );
};
